import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';

import { actions } from 'features/reports/redux';
import { actions as usersActions } from 'features/users/redux';
import { actions as treeActions } from 'features/tree';

import { searchInItems } from 'features/reports/data';

const useCashModel = () => {
  const dispatch = useDispatch();
  const locale = useSelector(state => state.locale.locale, shallowEqual);
  const { actionProcessing } = useSelector(state => state.reports, shallowEqual);

  const [count, setCount] = useState(10);
  const [page, setPage] = useState(0);
  const [isOnlyTotal, setIsOnlyTotal] = useState(true);

  const [date, setDate] = useState({
    beginDate: new Date(dayjs().add(-1, 'day').startOf('date')),
    endDate: new Date(dayjs().startOf('date')),
  });
  const [activeDay, setActiveDay] = useState('');

  const [filters, setFilters] = useState({
    role: searchInItems[0],
    user: '',
  });

  const [isDirectOnly, setIsDirectOnly] = useState(false);
  const [isHigher, setIsHigher] = useState(false);
  const [isDeposit, setIsDeposit] = useState(false);
  const [isWithdrawal, setIsWithdrawal] = useState(false);
  const [isBonus, setIsBonus] = useState(false);

  const [filterValue, setFilterValue] = useState('');
  const [searchResult, setSearchResult] = useState([]);
  const [isLoadingSearchResult, setIsLoadingSearchResult] = useState(false);

  const debouncedSearchUser = useRef(
    debounce(async (value, isDirectStructure) => {
      if (value.length >= 4) {
        setIsLoadingSearchResult(true);

        dispatch(
          usersActions.getUsersListForSearch({
            username: value,
            isDirectStructure,
            callback: data => {
              if (data) setSearchResult(data.map(i => i.username));
              setIsLoadingSearchResult(false);
            },
          }),
        );
      }
    }, 800),
  ).current;

  const handleSearchChange = e => {
    const { value } = e.target;

    setFilterValue(value);
    setFilters(prev => ({ ...prev, user: value }));

    if (value.length >= 4) {
      setIsLoadingSearchResult(true);
      debouncedSearchUser(value, isDirectOnly);
    } else {
      setIsLoadingSearchResult(false);
      setSearchResult([]);
    }
  };

  const handleResultClick = username => {
    setFilterValue(username);
    setFilters(prev => ({ ...prev, user: username }));
    setIsLoadingSearchResult(false);
    setSearchResult([]);
  };

  const handleSearch = (isTotal = isOnlyTotal) => {
    const requestedData = {
      isDirectOnly,
      isHigher,
      isDeposit,
      isWithdrawal,
      role: filters.role.value,
      username: filters.user,
      count,
      page,
      isBonus,
      beginDate: date.beginDate,
      endDate: date.endDate,
    };

    if (isTotal) {
      dispatch(actions.getPaymentHistoryTotals(requestedData));
    } else {
      dispatch(actions.getPaymentHistory(requestedData));
    }
  };

  const onClickSearch = () => {
    setIsOnlyTotal(true);
    setPage(0);
    dispatch(treeActions.setSearchTree(filters.user));
    handleSearch(true);
    setSearchResult([]);
  };

  const onItemsOnPageChanged = newItemsOnPage => {
    setCount(newItemsOnPage);
    setPage(0);
  };

  const onChangeBeginDate = value => {
    if (!isNaN(value.valueOf())) {
      setDate(prev => ({ ...prev, beginDate: value }));
      setActiveDay('');
    }
  };

  const onChangeEndDate = value => {
    if (!isNaN(value.valueOf())) {
      setDate(prev => ({ ...prev, endDate: value }));
    }
  };

  const onChangeActiveDay = dayFilter => {
    setActiveDay(dayFilter);
    setDate(prev => ({
      ...prev,
      beginDate: dayFilter.value,
      endDate: dayFilter.value2,
    }));
  };

  useEffect(() => {
    if (filterValue.length < 4) setSearchResult([]);
  }, [filterValue, JSON.stringify(searchResult)]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, page, isOnlyTotal]);

  useEffect(() => {
    dispatch(treeActions.setSearchTree(''));
  }, [dispatch]);

  return {
    actionProcessing,
    locale,

    count,
    page,
    isOnlyTotal,
    date,
    activeDay,
    filters,
    isDirectOnly,
    isHigher,
    isDeposit,
    isWithdrawal,
    isBonus,

    filterValue,
    searchResult,
    isLoadingSearchResult,

    onItemsOnPageChanged,
    setPage,
    setIsOnlyTotal,
    setFilters,
    setIsDirectOnly,
    setIsHigher,
    setIsDeposit,
    setIsWithdrawal,
    setIsBonus,
    onClickSearch,
    onChangeBeginDate,
    onChangeEndDate,
    onChangeActiveDay,
    handleSearchInChange: value => {
      const searchInItem = searchInItems.find(item => item.value === value);
      setFilters(prev => ({ ...prev, role: searchInItem }));
    },
    handleSearchChange,
    handleResultClick,
  };
};

export default useCashModel;
